<template>
  <div
    ref="containerElement"
    class="relative w-full overflow-hidden rounded-xl"
    :style="{ height: `${containerHeight ?? 0}px` }"
    :class="{ 'skeleton-loading': loading }"
  >
    <video
      v-if="bannerVideo && isOnline"
      class="relative z-20 mx-auto h-full"
      :class="shouldCrop ? 'w-full object-cover' : 'w-auto'"
      autoplay
      muted
      playsinline
      loop
      :poster="banner.url"
      width="100%"
      @playing="loading = false"
    >
      <source :src="bannerVideo.url" type="video/mp4" />
    </video>
    <img
      v-else
      :src="banner.url"
      alt=""
      class="relative z-20 mx-auto h-full"
      :class="shouldCrop ? 'w-full object-cover' : 'w-auto'"
      @load="loading = false"
    />

    <div
      v-if="!shouldCrop"
      :style="{ backgroundImage: `url('${banner.url}')` }"
      class="absolute inset-0 bg-center blur-2xl"
    />
  </div>
</template>

<script setup lang="ts">
import { useIsOnline } from '@menu/Composables/useIsOnline';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useEventListener } from '@/composables/useEventListener';
import getRestaurant from '@menu/Utils/getRestaurant';

const props = defineProps<{
  banner: {
    url: string;
    width: number;
    height: number;
  };
  bannerVideo: {
    url: string;
  } | null;
}>();

const { isOnline } = useIsOnline();
const restaurant = getRestaurant();

const loading = ref(true);
const containerWidth = ref<number | null>(null);
const windowHeight = ref<number | null>(null);

const containerHeight = computed(() => {
  if (shouldCrop.value && windowHeight.value && containerWidth.value) {
    return Math.min(
      (containerWidth.value / 1200) * 700,
      windowHeight.value * 0.5,
    );
  }

  if (windowHeight.value && containerWidth.value) {
    return Math.min(
      (containerWidth.value / props.banner.width) * props.banner.height,
      windowHeight.value * 0.5,
    );
  }

  return null;
});

const shouldCrop = computed(() => {
  return ['maisondecurry', 'BiceMareRestaurant'].includes(restaurant.slug);
});

const updateMeasurements = () => {
  containerWidth.value = getContainerWidth();
  windowHeight.value = getWindowHeight();

  // To fix the issue where user rotates device.
  setTimeout(() => {
    containerWidth.value = getContainerWidth();
    windowHeight.value = getWindowHeight();
  });
};

const containerElement = useTemplateRef('containerElement');

const getContainerWidth = () => {
  if (!containerElement.value) {
    throw new Error('Container element is missing.');
  }

  return containerElement.value.clientWidth;
};

const getWindowHeight = () => {
  const windowHeight = window.outerHeight;

  // On some iOS devices, window.outerHeight returns 0.
  // In that case, we'll use window.innerHeight instead.
  if (windowHeight === 0) {
    return window.innerHeight;
  }

  return windowHeight;
};

onMounted(updateMeasurements);
useEventListener(window, 'resize', updateMeasurements);
</script>
